import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useStore } from '../../lib/store';
import { prescriptionTimeline } from '../../data/prescriptionRecords';
import { XIcon, CheckCircleIcon, ClockIcon, PackageIcon, XCircleIcon, CalendarIcon, UserIcon, MapPinIcon, CreditCardIcon, FileTextIcon, TruckIcon, ExternalLinkIcon, StoreIcon } from 'lucide-react';
interface PrescriptionItem {
  name: string;
  quantity: number;
  price: number;
  image: string;
}
interface TrackingEvent {
  status: string;
  timestamp: string;
  description: string;
}
interface PrescriptionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  prescription: {
    id: string;
    date: string;
    status: string;
    items: PrescriptionItem[];
    doctor?: string;
    deliveryAddress?: string;
    deliveryCoordinates?: {
      lat: number;
      lng: number;
    };
    pickupBranch?: {
      name: string;
      address: string;
      coordinates: {
        lat: number;
        lng: number;
      };
    };
    pickupTime?: string;
    paymentMethod?: string;
    total?: number;
    prescriptionSource?: 'cash' | 'wasfaty' | 'insurance' | 'outpatient' | 'yusr';
    source?: 'cash' | 'wasfaty' | 'insurance' | 'outpatient' | 'yusr';
    insuranceProvider?: string;
    wasfatyNumber?: string;
    externalReference?: string;
    trackingHistory?: TrackingEvent[];
  };
  onCancelRequest?: (referenceNumber: string) => void;
}
export function PrescriptionDetailsModal({
  isOpen,
  onClose,
  prescription,
  onCancelRequest
}: PrescriptionDetailsModalProps) {
  const navigate = useNavigate();
  const addToCart = useStore(state => state.addToCart);
  if (!isOpen) return null;
  const stageIndex = getPrescriptionStageIndex(prescription.status);
  const reorder = () => {
    prescription.items.forEach((item, index) => {
      for (let quantity = 0; quantity < item.quantity; quantity += 1) {
        addToCart({
          id: Number(`86${String(index + 1)}${String(prescription.id.replace(/\D/g, '').slice(-4) || '0')}`),
          name: item.name,
          price: item.price / Math.max(1, item.quantity),
          image: item.image,
          requiresPrescription: true,
          prescriptionStatus: 'reviewing',
          availabilityStatus: 'available',
          nowEligible: true
        });
      }
    });
    toast.success('Items added for availability and prescription review');
    onClose();
    navigate('/cart?mode=now');
  };
  const reupload = () => {
    onClose();
    navigate('/prescription?type=cash');
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <CheckCircleIcon className="w-6 h-6 text-green-500" data-id="element-4676" data-mp="179cojpyqnceg" />;
      case 'ready':
        return <PackageIcon className="w-6 h-6 text-blue-500" data-id="element-4677" data-mp="1lxz8k4zb8sc0" />;
      case 'in-progress':
      case 'expired':
        return <ClockIcon className="w-6 h-6 text-yellow-500" data-id="element-4678" data-mp="1h1inxkrbkw3o" />;
      case 'insurance-rejected':
      default:
        return <XCircleIcon className="w-6 h-6 text-red-500" data-id="element-4679" data-mp="1uwuh3nptueih" />;
    }
  };
  const getStatusText = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'Delivered';
      case 'ready':
        return 'Ready for fulfillment';
      case 'in-progress':
        return 'Pharmacist review';
      case 'awaiting-approval':
        return 'Awaiting insurance approval';
      case 'out-for-delivery':
        return 'Out for delivery';
      case 'expired':
        return 'Prescription expired';
      case 'insurance-rejected':
        return 'Insurance claim rejected';
      case 'needs-contact':
        return 'Needs your attention';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Under review';
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'ready':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'out-for-delivery':
        return 'bg-sky-50 text-sky-700 border-sky-200';
      case 'in-progress':
      case 'awaiting-approval':
        return 'bg-amber-50 text-amber-800 border-amber-200';
      case 'expired':
        return 'bg-amber-50 text-amber-800 border-amber-200';
      case 'insurance-rejected':
      case 'needs-contact':
        return 'bg-rose-50 text-rose-700 border-rose-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };
  const getPrescriptionSourceBadge = () => {
    const source = prescription.source ?? prescription.prescriptionSource;
    if (!source) return null;
    const badges = {
      cash: {
        label: 'Cash prescription',
        color: 'bg-gray-100 text-gray-700'
      },
      wasfaty: {
        label: 'Wasfaty',
        color: 'bg-blue-100 text-blue-700'
      },
      insurance: {
        label: 'Insurance',
        color: 'bg-green-100 text-green-700'
      },
      outpatient: {
        label: 'Outpatient clinic',
        color: 'bg-rose-100 text-rose-700'
      },
      yusr: {
        label: 'Yusr',
        color: 'bg-violet-100 text-violet-700'
      }
    };
    const badge = badges[source];
    return <div className="mb-4" data-id="element-4680" data-mp="26j9mn85551sd">
        <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold ${badge.color}`} data-id="element-4681" data-mp="26jhxkuhpdx0x">
          <FileTextIcon className="w-3.5 h-3.5" data-id="element-4682" data-mp="3kgkkx79l8921" />
          {badge.label}
          {source === 'wasfaty' && prescription.wasfatyNumber && <span className="ml-1" data-id="element-4683" data-mp="3pedso4qjhb4f">• {prescription.wasfatyNumber}</span>}
          {source === 'insurance' && prescription.insuranceProvider && <span className="ml-1" data-id="element-4684" data-mp="3u6xe1nvgxajs">• {prescription.insuranceProvider}</span>}
          {(source === 'outpatient' || source === 'yusr') && prescription.externalReference && <span className="ml-1" data-id="element-4685" data-mp="31i891drakl2r">• {prescription.externalReference}</span>}
        </span>
      </div>;
  };
  const getGoogleMapsLink = (coordinates?: {
    lat: number;
    lng: number;
  }, address?: string) => {
    if (coordinates) {
      return `https://www.google.com/maps/dir/?api=1&destination=${coordinates.lat},${coordinates.lng}`;
    }
    if (address) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    }
    return null;
  };
  return <div className="fixed inset-0 z-[200] flex items-end bg-black/45 sm:items-center sm:justify-center sm:p-4" role="dialog" aria-modal="true" aria-label="Prescription details" data-id="element-4686" data-mp="uppcyvbbaxza">
      <button type="button" className="absolute inset-0" onClick={onClose} aria-label="Close prescription details" data-id="element-4687" data-mp="2mtbipcys0z5f" />
      <div className="relative max-h-[90vh] w-full overflow-y-auto rounded-t-3xl bg-white text-left shadow-2xl sm:max-w-3xl sm:rounded-2xl" data-id="element-4688" data-mp="30r06ask8ake6">
        <div className="sticky top-0 z-20 bg-white px-6 pt-2 sm:hidden" data-id="element-4689" data-mp="2vrf7ci526c1g"><div className="mx-auto h-1 w-10 rounded-full bg-gray-300" data-id="element-4690" data-mp="284fc7kcdbu6x" /></div>
          {/* Header */}
          <div className="sticky top-0 z-10 bg-[#1E2B55] px-6 py-4" data-id="element-4691" data-mp="2365i7d2dqzmw">
            <div className="flex items-center justify-between" data-id="element-4692" data-mp="2htqkj4rfkutr">
              <div data-id="element-4693" data-mp="2hv9z8qxfuxtf">
                <h3 className="text-xl font-bold text-white" data-id="element-4694" data-mp="2d28jyhtbkw9b">
                  Prescription Details
                </h3>
                <p className="text-sm text-blue-100 mt-1" data-id="element-4695" data-mp="1pc4hwevs54kf">{prescription.id}</p>
              </div>
              <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors" data-id="element-4696" data-mp="1kd1ndodfegqp">
                <XIcon className="w-6 h-6" data-id="element-4697" data-mp="3vzlk78d4a2z7" />
              </button>
            </div>
          </div>
          {/* Content */}
          <div className="px-6 py-6 space-y-6" data-id="element-4698" data-mp="3n19ynkr3xvdm">
            {/* Prescription Source Badge */}
            {getPrescriptionSourceBadge()}
            {/* Status */}
            <div className={`flex items-center justify-between p-4 rounded-lg border ${getStatusColor(prescription.status)}`} data-id="element-4699" data-mp="3r092f213rm3h">
              <div className="flex items-center" data-id="element-4700" data-mp="nkk0u8s16xaj">
                {getStatusIcon(prescription.status)}
                <div className="ml-3" data-id="element-4701" data-mp="9ksixwye8wr9">
                  <p className="font-bold" data-id="element-4702" data-mp="9ny97fire4ok">
                    {getStatusText(prescription.status)}
                  </p>
                  <p className="text-sm" data-id="element-4703" data-mp="2ucch5juq02lx">
                    {new Date(prescription.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                  </p>
                </div>
              </div>
              <CalendarIcon className="w-5 h-5" data-id="element-4704" data-mp="3lze4g3mg3jb5" />
            </div>
            {/* Prescribed Items with Images and Prices */}
            <div data-id="element-4705" data-mp="3h12vdv3z5ctz">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-4706" data-mp="3h2nfeqsnxddl">
                <FileTextIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-4707" data-mp="3c310mcx6rp2o" />
                Prescribed Items
              </h4>
              <div className="space-y-3" data-id="element-4708" data-mp="dlx1zqur3y1v">
                {prescription.items.map((item, index) => <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-4709" data-mp="8n3ovzjdm928" data-mpi={index}>
                    <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded-lg" data-id="element-4710" data-mp="3v028q6m1e5au" />
                    <div className="flex-1" data-id="element-4711" data-mp="ri0wzmdg8afe">
                      <h5 className="font-medium text-gray-900 mb-1" data-id="element-4712" data-mp="yv01e5qi0n28">
                        {item.name}
                      </h5>
                      <p className="text-sm text-gray-600" data-id="element-4713" data-mp="13uw66770siiu">
                        Quantity: {item.quantity}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-4714" data-mp="1ds0sq84s2g06">
                      <p className="text-lg font-bold text-[#1E2B55]" data-id="element-4715" data-mp="1dtlr68u7qj3b">
                        SAR {item.price.toFixed(2)}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Doctor Info */}
            {prescription.doctor && <div data-id="element-4716" data-mp="1rlie7yh0y2su">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-4717" data-mp="1wiijw89yp3jf">
                  <UserIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-4718" data-mp="1wkld116evcsd" />
                  Prescribed By
                </h4>
                <div className="p-3 bg-gray-50 rounded-lg" data-id="element-4719" data-mp="25ix9vkxvf6t9">
                  <p className="text-sm text-gray-700" data-id="element-4720" data-mp="25jgha7pwact1">{prescription.doctor}</p>
                </div>
              </div>}
            {/* Delivery Address or Pickup Branch */}
            {prescription.deliveryAddress && <div data-id="element-4721" data-mp="3gy84axv9bt13">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-4722" data-mp="3gxokfbm1qukf">
                  <MapPinIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-4723" data-mp="3loercta4f5h8" />
                  Delivery Address
                </h4>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-4724" data-mp="3qmq0f1sldbye">
                  <p className="text-sm text-gray-700 mb-3" data-id="element-4725" data-mp="3uotmslgsu0yg">
                    {prescription.deliveryAddress}
                  </p>
                  {getGoogleMapsLink(prescription.deliveryCoordinates, prescription.deliveryAddress) && <a href={getGoogleMapsLink(prescription.deliveryCoordinates, prescription.deliveryAddress)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#1E2B55] hover:text-[#fdc300] font-medium text-sm" data-id="element-4726" data-mp="25b82uwl6itig">
                      <MapPinIcon className="w-4 h-4" data-id="element-4727" data-mp="1t0p8ig22kfnu" />
                      View on Google Maps
                      <ExternalLinkIcon className="w-4 h-4" data-id="element-4728" data-mp="1o2nkdgpsdq5i" />
                    </a>}
                </div>
              </div>}
            {prescription.pickupBranch && <div data-id="element-4729" data-mp="2gqjgokbg1g1r">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-4730" data-mp="2gnxc8tjungnr">
                  <StoreIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-4731" data-mp="2bou6f6w1p6eq" />
                  Pickup Branch
                </h4>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-4732" data-mp="10bi8fcmyvi0h">
                  <p className="font-medium text-gray-900 mb-1" data-id="element-4733" data-mp="109f9g33u6ejm">
                    {prescription.pickupBranch.name}
                  </p>
                  <p className="text-sm text-gray-700 mb-3" data-id="element-4734" data-mp="3jhugm9htvuii">
                    {prescription.pickupBranch.address}
                  </p>
                  {prescription.pickupTime && <div className="flex items-center gap-2 mb-3 p-2 bg-blue-50 rounded-lg" data-id="element-4735" data-mp="39kg7yw25ca0r">
                      <ClockIcon className="w-4 h-4 text-blue-600" data-id="element-4736" data-mp="3ej0zm73c1ldl" />
                      <span className="text-sm font-medium text-blue-900" data-id="element-4737" data-mp="3eldqj9r2j62s">
                        Pickup Time: {prescription.pickupTime}
                      </span>
                    </div>}
                  <a href={getGoogleMapsLink(prescription.pickupBranch.coordinates, prescription.pickupBranch.address)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#1E2B55] hover:text-[#fdc300] font-medium text-sm" data-id="element-4738" data-mp="34mxzt7vydytt">
                    <MapPinIcon className="w-4 h-4" data-id="element-4739" data-mp="228aa7fxlflq8" />
                    View on Google Maps
                    <ExternalLinkIcon className="w-4 h-4" data-id="element-4740" data-mp="1y7i4gwnxjyyp" />
                  </a>
                </div>
              </div>}
            <div data-id="element-4741" data-mp="2c7sujxhf19it">
              <h4 className="mb-3 font-bold text-gray-900" data-id="element-4742" data-mp="2c8ay7f6eqq0r">Prescription progress</h4>
              <ol className="space-y-3" data-id="element-4743" data-mp="27750t41syekw">{prescriptionTimeline.map((stage, index) => {
              const complete = index <= stageIndex;
              const active = index === stageIndex;
              return <li key={stage} className="flex gap-3" data-id="element-4744" data-mp="2h5b1jibvheo6" data-mpi={index}><div className="flex flex-col items-center" data-id="element-4745" data-mp="2h49q3cqy2q5x"><span className={`flex h-6 w-6 items-center justify-center rounded-full ${complete ? 'bg-[#16726c] text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-4746" data-mp="2uwfw460dvgrn">{complete ? <CheckCircleIcon className="h-4 w-4" data-id="element-4747" data-mp="2q0qwy3sa01vv" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" data-id="element-4748" data-mp="34wqbnx9w509z" />}</span>{index < prescriptionTimeline.length - 1 && <span className={`mt-1 h-5 w-px ${index < stageIndex ? 'bg-[#16726c]' : 'bg-gray-200'}`} data-id="element-4749" data-mp="2zxn86h9k7244" />}</div><div className="pb-1" data-id="element-4750" data-mp="x8vobus4abfu"><p className={`text-sm font-bold ${active ? 'text-[#16726c]' : 'text-gray-700'}`} data-id="element-4751" data-mp="x9do31dzn7si">{stage}</p>{active && <p className="mt-0.5 text-xs text-gray-500" data-id="element-4752" data-mp="1276wm12rkdvy">{getStatusText(prescription.status)}</p>}</div></li>;
            })}</ol>
            </div>
            {(prescription.status === 'expired' || prescription.status === 'insurance-rejected') && <div className="rounded-xl border border-rose-200 bg-rose-50 p-3" data-id="element-4753" data-mp="ebavkcm8espy"><p className="text-sm font-bold text-rose-800" data-id="element-4754" data-mp="ecvpq14mw9na">{prescription.status === 'expired' ? 'This prescription has expired.' : 'Insurance approval was not granted.'}</p><p className="mt-1 text-xs leading-5 text-rose-700" data-id="element-4755" data-mp="o8z0twhgrimd">{prescription.status === 'expired' ? 'Upload a renewed prescription to continue.' : 'You can re-upload the prescription or continue with another payment method.'}</p></div>}
            {/* Tracking History or Pickup Time */}
            {prescription.trackingHistory && prescription.trackingHistory.length > 0 && <div data-id="element-4756" data-mp="3p5sqxlpx22ak">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-4757" data-mp="3p4zrz6x78h2z">
                    <TruckIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-4758" data-mp="3k7hhvd7g3vte" />
                    Delivery Tracking
                  </h4>
                  <div className="space-y-4" data-id="element-4759" data-mp="3fdva9zg52i4g">
                    {prescription.trackingHistory.map((event, index) => <div key={index} className="flex gap-4" data-id="element-4760" data-mp="35hjydakgnuaj" data-mpi={index}>
                        <div className="flex flex-col items-center" data-id="element-4761" data-mp="35j3f12a147cu">
                          <div className={`w-3 h-3 rounded-full ${index === 0 ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-4762" data-mp="31fowa3uphcvp" />
                          {index < prescription.trackingHistory.length - 1 && <div className="w-0.5 h-full bg-gray-300 mt-1" data-id="element-4763" data-mp="2rk3r5wucbwhq" />}
                        </div>
                        <div className="flex-1 pb-4" data-id="element-4764" data-mp="ppw5prchcnxj">
                          <div className="flex items-center justify-between mb-1" data-id="element-4765" data-mp="pob97w6luux1">
                            <p className={`font-medium ${index === 0 ? 'text-green-700' : 'text-gray-700'}`} data-id="element-4766" data-mp="2k51ltyelo6be">
                              {event.status}
                            </p>
                            <span className="text-xs text-gray-500" data-id="element-4767" data-mp="2f79sczyb4c53">
                              {new Date(event.timestamp).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600" data-id="element-4768" data-mp="1hi7vjccqzuor">
                            {event.description}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </div>}
            {/* Payment Method */}
            {prescription.paymentMethod && <div data-id="element-4769" data-mp="dmfdiccb9gi4">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-4770" data-mp="dkm8t2l9d6cr">
                  <CreditCardIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-4771" data-mp="9k2avwsv1ypk" />
                  Payment Method
                </h4>
                <div className="p-3 bg-gray-50 rounded-lg" data-id="element-4772" data-mp="3i0nc2rf93ni4">
                  <p className="text-sm text-gray-700" data-id="element-4773" data-mp="3d1khy20t71ox">
                    {prescription.paymentMethod}
                  </p>
                </div>
              </div>}
            {/* Total */}
            {prescription.total && <div className="border-t border-gray-200 pt-4" data-id="element-4774" data-mp="3m0foxgbogfta">
                <div className="flex items-center justify-between" data-id="element-4775" data-mp="2ybcwrejf34kj">
                  <span className="text-lg font-bold text-gray-900" data-id="element-4776" data-mp="2uaj8ul5z5prn">Total</span>
                  <span className="text-2xl font-bold text-[#1E2B55]" data-id="element-4777" data-mp="2u8glk8w7iaqm">
                    SAR {prescription.total.toFixed(2)}
                  </span>
                </div>
              </div>}
          </div>
          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex gap-3 sticky bottom-0" data-id="element-4778" data-mp="3s5qv267z8i53">
            <button onClick={onClose} className="flex-1 bg-white border border-gray-300 text-gray-700 font-medium py-2.5 px-4 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4779" data-mp="zh2ttkuvx2g">
              Close
            </button>
            {prescription.status === 'in-progress' && onCancelRequest && <button type="button" onClick={() => onCancelRequest(prescription.id)} className="flex-1 rounded-lg border border-red-200 bg-white px-4 py-2.5 font-medium text-red-600 transition-colors duration-150 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600" data-id="element-4780" data-mp="fpod3m0pm9s2">
                Cancel request
              </button>}
            {prescription.status === 'delivered' && <button type="button" onClick={reorder} className="flex-1 rounded-lg bg-[#16726c] px-4 py-2.5 font-medium text-white transition-[background-color] duration-150 hover:bg-[#105b56]" data-id="element-4781" data-mp="ysp3uudicw9j">Order again</button>}
            {prescription.status === 'expired' && <button type="button" onClick={reupload} className="flex-1 rounded-lg bg-[#16726c] px-4 py-2.5 font-medium text-white transition-[background-color] duration-150 hover:bg-[#105b56]" data-id="element-4782" data-mp="pud4a2unyo4y">Re-upload prescription</button>}
            {prescription.status === 'insurance-rejected' && <button type="button" onClick={reupload} className="flex-1 rounded-lg bg-[#16726c] px-4 py-2.5 font-medium text-white transition-[background-color] duration-150 hover:bg-[#105b56]" data-id="element-4783" data-mp="1cryab2bwwc0b">Re-upload prescription</button>}
          </div>
        </div>
    </div>;
}
function getPrescriptionStageIndex(status: string) {
  if (status === 'delivered' || status === 'out-for-delivery' || status === 'ready') return 4;
  if (status === 'in-progress' || status === 'awaiting-approval') return 1;
  if (status === 'insurance-rejected' || status === 'expired' || status === 'needs-contact') return 1;
  return 0;
}